import { Button, type ButtonProps, IconButton, LuIcon } from "@overmap-ai/blocks"
import type { Issue, IssueType, Stored } from "@overmap-ai/common"
import { useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectActiveProjectId } from "../../store/selectors"

interface AddIssueButtonProps extends Omit<ButtonProps, "children"> {
	issueValues?: Partial<Issue>
	issueType: Stored<IssueType>
	compact?: boolean
}

export const AddIssueButton = memo((props: AddIssueButtonProps) => {
	const { issueValues, issueType, compact = false, ...rest } = props
	const projectId = useAppSelector(selectActiveProjectId)
	const navigate = useNavigate()

	const handleClick = useCallback(() => {
		if (!projectId) return

		const [offlineIssue, _promise] = sdk.issues.add({
			title: "",
			assigned_to: null,
			due_date: null,
			description: null,
			project: projectId,
			issue_type: issueType.uuid,
			status: issueType.default_status!,
			priority: issueType.default_priority!,
			...issueValues,
		})

		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/issues/$issueId",
			params: (prev) => ({
				...prev,
				issueId: offlineIssue.uuid,
			}),
		})
	}, [issueType.uuid, issueValues, navigate, projectId, issueType.default_priority, issueType.default_status])

	const ButtonComponent = compact ? IconButton : Button

	return (
		<ButtonComponent onClick={handleClick} aria-label="add issue" {...rest}>
			<LuIcon icon={modelIconMapping.add} />
			{!compact && "Add"}
		</ButtonComponent>
	)
})
AddIssueButton.displayName = "AddIssueButton"
